import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { ApolloError } from 'apollo-client';
import { useApolloClient, useMutation } from '@apollo/react-hooks';
import { FormFieldData, FormProvider } from 'modules/forms';
import { ReadOn, useAdminRights, usePermissions, UserPermission, WriteOn } from 'modules/permissions';
import { ConfigContext, IAppConfig, IConfig, useConfig } from 'modules/config';
import { useRestClient } from 'modules/rest-client';
import { ActionResponse, ErrorListToastMessage } from 'modules/errors';
import { IFormIsValidFunction } from 'modules/forms';
import GraphQlErrorBoundary from 'components/layout/ErrorBoundary';
import Loading from 'components/layout/Loading';
import { tryParseInt } from 'shared/utils/string.utils';
import { isNullOrUndefined } from 'shared/utils/null.utils';
import { ConditionalRender } from 'components/layout/ConditionalRender';
import { IssueDisplayEnum } from './enums/issue-display.enum';
import { GetIssueById } from './queries/view-issue.queries';
import { GetIssueByIdVariables, Issue, IssueByIdResponse } from './queries/view-issue.queries.types';
import { StyledIssueContainer } from './styled/ViewAmmendIssue.styled';
import { ammendIssueOptions } from './options/ammend-issue.options';
import { IssueEntityMapper } from './mappers/IssueEntityMapper';
import {
   UpdateIssueResponse,
   UpdateIssueVariables,
   IDeleteFileVariables,
   IUpdateFileVariables,
   ReopenIssueResponse,
   ReopenIssueVariables
} from './mutations/ammend-issue.mutations.types';
import { UpdateIssue, DeleteFile, UpdateFile, ReopenIssue } from './mutations/ammend-issue.mutations';
import { UpdateIssueRequestMapper } from './mappers/UpdateIssueMapper';
import { ObservationStatusEnum } from 'shared/enums/ObservationStatus.enum';
import { useFiles } from 'shared/utils/files/hooks/useFiles';
import { AmmendIssueLoadingState } from './enums/ammend-near-miss-loading-state.enum';
import { ObservationFileEntity } from 'shared/types/file/ObservationFileEntity';
import { allowedMimeTypes } from 'shared/options/observation-files.options';
import { mapFileToFormData } from 'shared/utils/mapping.utils';
import { IFileUploadResponse } from 'shared/types/file/IFileUploadRequest';
import { GetObservationPhotoFiles } from 'components/ObservationPhoto/queries/observation-photos.queries';
import { GetObservationPhotoFileVariables } from 'components/ObservationPhoto/queries/observation-photos.queries.types';
import { ObservationFileMapper } from 'shared/mappers/ObservationFileMapper';
import { GetFilesByReferenceResponse } from 'shared/types/file/observation-file.type';
import { FileStatus } from 'shared/enums/FileStatus.enum';
import { getErrorMessages } from 'shared/utils/errors/errors.utils';
import AuditContainer from 'components/AuditContainer';
import IssueHeader from './components/IssueHeader';
import IssueForbidden from './components/IssueForbidden';
import IssueSubHeader from './components/IssueSubHeader';
import AmmendIssue from './components/AmmendIssueForm';
import ViewIssuesCompletion from './components/IssueCompletion';
import { IssueEntity } from 'shared/types/domain/IssueEntity';

export const ViewAmmendIssue = () => {
   const { settings } = useConfig<IAppConfig>();
   const { params } = useRouteMatch<{ id: string }>();
   const restClient = useRestClient<'fileUpload'>();
   const client = useApolloClient();
   const [isLoading, setIsLoading] = useState<boolean>(true);
   const [loadingMessage, setLoadingMessage] = useState<AmmendIssueLoadingState>(
      AmmendIssueLoadingState.loadingIssue
   );
   const [loadingError, setLoadingError] = useState<ApolloError>();

   const { hasPermission } = usePermissions();
   const { hasAdminRights } = useAdminRights();

   const { addToast } = useToasts();

   const [issue, setIssue] = useState<Issue>();
   const [issueForm, setIssueForm] = useState<FormFieldData<IssueEntity> | undefined>();
   const [updateFileMutation] = useMutation<{ updateDocument: boolean }, IUpdateFileVariables>(UpdateFile);
   const [deleteFileMutation] = useMutation<{ deleteDocument: boolean }, IDeleteFileVariables>(DeleteFile);

   const { files, addFiles, updateFiles, removeFiles, setData } = useFiles<ObservationFileEntity>({
      allowedMimeTypes,
      onNotAllowed: handleNotAllowedFilesAdded,
      addFile: uploadFile,
      updateFile: updateFile,
      removeFile: deleteFile,
      maxFileUploadBytes: settings?.MaxFileUploadBytes
   });

   function handleNotAllowedFilesAdded(messages: string[]): void {
      addToast(
         <ErrorListToastMessage
            errors={messages}
            baseMessage={'There were disallowed file types in the collection of files uploaded'}
         />,
         { appearance: 'warning' }
      );
   }

   useEffect(() => {
      loadFiles();
   }, [issue]);

   const loadFiles = async () => {
      setIsLoading(true);
      if (!issue) {
         return;
      }
      try {
         const result = await loadIssueImages(issue.uniqueRecordId);

         if (!isNullOrUndefined(result)) {
            const emailedFiles = issue.emailedFileIds;
            if (!isNullOrUndefined(emailedFiles)) {
               result.forEach(f => {
                  if (emailedFiles.indexOf(f.fileId as number) !== -1) {
                     f.emailEvidence = true;
                  }
               });
            }

            await setData(result);
         }
      } catch (e) {
         setLoadingError(e as ApolloError);
      } finally {
         setIsLoading(false);
      }
   };

   async function loadIssueImages(uniqueRecordId: UID): Promise<ObservationFileEntity[] | null> {
      try {
         const result = await client.query<GetFilesByReferenceResponse, GetObservationPhotoFileVariables>({
            query: GetObservationPhotoFiles,
            variables: {
               id: uniqueRecordId
            },
            fetchPolicy: 'network-only'
         });
         if (result.errors) return null;

         const mapper = new ObservationFileMapper();
         return mapper.map(result.data);
      } catch (error) {
         setLoadingError(error as ApolloError);
         return null;
      }
   }

   async function uploadFile(
      file: ObservationFileEntity,
      folderName?: string,
      data?: UID
   ): Promise<ObservationFileEntity> {
      const formData = mapFileToFormData<{ referenceId: UID | undefined }>(file, settings, folderName, {
         referenceId: data
      });

      const endpoint = restClient.getEndpoint('fileUpload');
      const result = await endpoint.post<IFileUploadResponse>({
         method: 'UploadFile',
         data: formData,
         onUploadProgress: p => {}
      });

      return { ...file, fileId: result.data.fileId };
   }

   async function updateFile(file: ObservationFileEntity, data: UID): Promise<void> {
      await updateFileMutation({
         variables: {
            request: {
               comment: file.comment ?? '',
               documentTypeId: file.type?.id ?? 6,
               fileId: file.fileId!,
               referenceId: data
            }
         }
      });
   }

   const [displayMode, setDisplayMode] = useState<IssueDisplayEnum>(IssueDisplayEnum.Overview);

   const setLoadingState = (loadingMessage: AmmendIssueLoadingState, isLoading: boolean): void => {
      setLoadingMessage(loadingMessage);
      setIsLoading(isLoading);
   };

   async function deleteFile(file: ObservationFileEntity): Promise<void> {
      await deleteFileMutation({
         variables: {
            referenceId: issue?.uniqueRecordId ?? '',
            fileId: file.fileId!
         }
      });
   }

   const loadEntity = useCallback(async (): Promise<void> => {
      const entityId = tryParseInt(params.id);

      if (!isNullOrUndefined(entityId)) {
         setLoadingState(AmmendIssueLoadingState.loadingIssue, true);

         try {
            let result = await client.query<IssueByIdResponse, GetIssueByIdVariables>({
               query: GetIssueById,
               variables: {
                  id: entityId
               },
               fetchPolicy: 'network-only'
            });

            setIssue(result.data.issue);

            if (result?.data != null) {
               const mapper = new IssueEntityMapper();

               setIssueForm(mapper.map(result?.data));
            } else {
               setIssueForm(undefined);
               setIssue(undefined);
            }
         } catch (e) {
            setLoadingError(e as ApolloError);
         } finally {
            setLoadingState(AmmendIssueLoadingState.loadingIssue, false);
         }
      }
   }, [params.id, client]);

   const handleSaveChanges = async (
      formData: FormFieldData<IssueEntity> | undefined,
      fileData: ObservationFileEntity[] | undefined,
      isValid: IFormIsValidFunction
   ): Promise<ActionResponse> => {
      setLoadingState(AmmendIssueLoadingState.savingChanges, true);

      const response = await updateIssueEntity(formData, isValid);

      if (fileData) {
         fileData
            .filter(f => f.fileStatus === FileStatus.Removed)
            .forEach(f => {
               deleteFile(f);
            });
      }

      setLoadingState(AmmendIssueLoadingState.savingChanges, false);

      if (!response.successful) {
         addToast(<ErrorListToastMessage errors={response.getErrorMessages()} />, { appearance: 'error' });
      } else {
         addToast('Changes saved successfully', { appearance: 'success' });

         loadEntity();
      }

      return response;
   };

   const updateIssueEntity = async (
      formData: FormFieldData<IssueEntity> | undefined,
      isValid: IFormIsValidFunction
   ): Promise<ActionResponse> => {
      const actionResponse = new ActionResponse();

      if (isNullOrUndefined(formData) || !isValid(true)) {
         return actionResponse.set({
            successful: false,
            error: {
               message: 'Some of the information entered was not valid, please fix any validation errors before continuing'
            }
         });
      }

      if (issue?.observationStatus !== ObservationStatusEnum.Open) {
         return actionResponse.set({
            successful: false,
            error: {
               message: "You are attempting to amend a closed Issue which is prohibited."
            }
         });
      }

      if (
         !hasPermission(WriteOn(UserPermission.ExistingObservation)) &&
         !hasPermission(WriteOn(UserPermission.IncidentDate)) &&
         !hasPermission(WriteOn(UserPermission.ObservationReporter))
      ) {
         return actionResponse.set({
            successful: false,
            error: {
               message: 'You do not have the required permissions to perform this action.'
            }
         });
      }

      const mapper = new UpdateIssueRequestMapper();
      const requestObject = mapper.map(formData);

       try {
         await client.mutate<UpdateIssueResponse, UpdateIssueVariables>({
            mutation: UpdateIssue,
            variables: {
               request: requestObject
            }
         });

         actionResponse.set({ successful: true });
      } catch (error) {
         actionResponse.set({
            successful: false,
            error: {
               message: {
                  baseMessage: "Issue updating issue.",
                  messages: getErrorMessages(error as ApolloError)
               }
            }
         });
      }

      return actionResponse;
   };

   const handleCompleteIssue = () => {
      loadEntity();
   };

   const handleReOpenIssue = async () => {
      setLoadingState(AmmendIssueLoadingState.submittingReOpening, true);
      const actionResponse = new ActionResponse();

      if (issue?.observationStatus == ObservationStatusEnum.Open) {
         setLoadingState(AmmendIssueLoadingState.submittingReOpening, false);
         return actionResponse.set({
            successful: false,
            error: {
               message: 'The near miss is already open'
            }
         });
      }

      if (!hasPermission(WriteOn(UserPermission.ReOpen))) {
         setLoadingState(AmmendIssueLoadingState.submittingReOpening, false);
         return actionResponse.set({
            successful: false,
            error: {
               message: 'You do not have the required permissions to perform this action.'
            }
         });
      }

      try {
         await client.mutate<ReopenIssueResponse, ReopenIssueVariables>({
            mutation: ReopenIssue,
            variables: {
               nearMissId: issue?.observationId ?? 0
            }
         });

         loadEntity();

         actionResponse.set({ successful: true });
      } catch (error: any) {
         actionResponse.set({
            successful: false,
            error: {
               message: 'There was an issue re-opening the Issue',
               error: error
            }
         });
      }

      const response = actionResponse;

      setLoadingState(AmmendIssueLoadingState.submittingReOpening, false);

      if (!response.successful) {
         addToast(<ErrorListToastMessage errors={response.getErrorMessages()} />, { appearance: 'error' });
      } else {
         addToast('Changes saved successfully', { appearance: 'success' });
      }

      return response;
   };

   useEffect(() => {
      loadEntity();
   }, [loadEntity]);

   const goToOverview = (): void => {
      setDisplayMode(IssueDisplayEnum.Overview);
   };

   const goToCompletionDetails = (): void => {
      if (issue?.completion) {
         setDisplayMode(IssueDisplayEnum.Completion);
      }
   };

   const goToAuditLog = (): void => {
      setDisplayMode(IssueDisplayEnum.Audit);
   };

   return (
      <React.Fragment>
         <Loading isLoading={isLoading} message={loadingMessage} noDelay>
            <GraphQlErrorBoundary error={loadingError}>
               <StyledIssueContainer>
                  <ConditionalRender condition={hasAdminRights} elseRender={() => <IssueForbidden />}>
                     <FormProvider<IssueEntity> options={ammendIssueOptions} formFields={issueForm} useEnglish>
                        <IssueHeader
                           issue={issue}
                           onSave={handleSaveChanges}
                           onComplete={handleCompleteIssue}
                           onReOpen={handleReOpenIssue}
                           setLoadingState={setLoadingState}
                           isLoading={isLoading}
                           files={files}
                           handleAddFile={addFiles}
                           handleUpdateFiles={updateFiles}
                           handleDeleteEvidence={removeFiles}
                        />

                        <IssueSubHeader
                           issue={issue}
                           displayMode={displayMode}
                           formHasErrors={false}
                           goToOverview={goToOverview}
                           goToCompletionDetails={goToCompletionDetails}
                           goToAuditLog={goToAuditLog}
                        />

                        <ConditionalRender condition={displayMode == IssueDisplayEnum.Overview}>
                           <AmmendIssue issue={issue} removeFiles={removeFiles} files={files} />
                        </ConditionalRender>

                        <ConditionalRender condition={displayMode === IssueDisplayEnum.Completion}>
                           <ViewIssuesCompletion
                              observationUniqueId={issue?.uniqueRecordId ?? ''}
                              issueCompletion={issue?.completion}
                              files={files.filter(x => x.type?.id === 11)}
                           />
                        </ConditionalRender>

                        <ConditionalRender
                           condition={
                              displayMode == IssueDisplayEnum.Audit && hasPermission(ReadOn(UserPermission.Audit))
                           }
                        >
                           {issue && <AuditContainer 
                              uniqueRecordId={issue.uniqueRecordId}
                              useEnglish={true}
                           />}
                        </ConditionalRender>
                     </FormProvider>
                  </ConditionalRender>
               </StyledIssueContainer>
            </GraphQlErrorBoundary>
         </Loading>
      </React.Fragment>
   );
};
